import React, { FC, ReactNode, useCallback, useEffect, useState } from "react"
import {
    Button,
    Dialog,
    FormattedMessage,
    Grid,
    GridRow,
    Icon,
    Input,
    InputProps,
    PageTitle,
    useIntl,
    DialogDescription,
    DateTimeInput,
    Checkbox,
} from "@metsooutotec/modes-react-components"
import { CreateSensorBatch, EditSensorBatch, SensorBatch } from "../models/sensor-batch-models"
import { handleUploadFileClick, NavigationModalOptions, resetRecordFormData } from "../../../../components/utils/modal-helper"
import { validateText } from "../../../../validators/inputValidators"
import sensorBatchStore from "../stores/sensor-batch-store"
import { StoreResults } from "../../../../stores/stores-helper"
import { convertDateToDDMMMYYYY, getTodayDateString } from "../../../../components/utils/date-formatter-utils"
import styles from "./sensor-batch-modal.module.css"

interface SensorBatchModalProps {
    isOpen: boolean | undefined
    onClose: (passed: boolean, customerId?: string, navigationSensorBatchOpetions?: NavigationModalOptions) => void
    sensorBatch?: SensorBatch
}
interface SensorBatchFormField {
    id: string
    label: string
    placeholder: string
    type: string
    validate?: (value: string) => string
    pattern?: string
    step?: string
    onFileUpload?: () => void
    unSelectFile?: () => void
    isRequired: boolean
}

const SensorBatchModal: FC<SensorBatchModalProps> = ({ isOpen, onClose, sensorBatch }) => {
    const { formatMessage } = useIntl()
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<{
        isError: boolean
        errorMessage: string
    }>({ isError: false, errorMessage: "" })
    const [isEditing, setIsEditing] = useState<boolean>(Boolean(sensorBatch?.id))
    const [isFormModified, setIsFormModified] = useState<boolean>(false)
    const [datePickerError, setDatePickerError] = useState<{
        isError: boolean
        errorMessage: string
    }>({ isError: false, errorMessage: "" })
    const [batchDate, setBatchDate] = useState<string>(isEditing ? convertDateToDDMMMYYYY(sensorBatch?.batchDate as Date) : getTodayDateString())
    const [selectedPdf, setSelectedPdf] = useState<File | null>(null)
    const [selectedCSV, setSelectedCSV] = useState<File | null>(null)
    const [formData, setFormData] = useState<Record<string, string>>({
        purchaseOrder: sensorBatch?.purchaseOrder || "",
        manufacturer: sensorBatch?.manufacturer || "",
        description: sensorBatch?.description || "",
        partNumber: sensorBatch?.partNumber || "",
        isVirtualSensorBatch: sensorBatch?.isVirtualSensorBatch?.toString() ?? false.toString(),
        uploadPdfName: selectedPdf?.name as string,
        uploadCSVName: selectedCSV?.name as string,
    })

    const [formErrors, setFormErrors] = useState<Record<string, string>>({
        purchaseOrder: "",
        manufacturer: "",
        partNumber: "",
    })

    const modalTitle = isEditing ? formatMessage("sensorBatchModal.editTitle") : formatMessage("sensorBatchModal.createTitle")

    useEffect(() => {
        if (sensorBatch) {
            const isSameSite = sensorBatch.id === formData.id
            if (isSameSite) return

            setIsEditing(Boolean(sensorBatch.id))
            setFormData({
                id: sensorBatch.id,
                purchaseOrder: sensorBatch?.purchaseOrder || "",
                manufacturer: sensorBatch?.manufacturer || "",
                description: sensorBatch?.description || "",
                partNumber: sensorBatch?.partNumber?.toString() || "",
                isVirtualSensorBatch: sensorBatch?.isVirtualSensorBatch?.toString() ?? false.toString(),
            })
        } else {
            setIsEditing(false)
            resetRecordFormData(setFormData)
        }
        // Reset the form modification state when the asset changes
        setIsFormModified(false)
    }, [sensorBatch, formData.id])

    const validateForm = (formData: Record<string, string>): Record<string, string> => {
        const errors: Record<string, string> = {}
        errors.purchaseOrder = validateText(formData.purchaseOrder)
        errors.manufacturer = validateText(formData.manufacturer)
        errors.partNumber = validateText(formData.partNumber)
        return errors
    }
    const handleValidation = useCallback((fieldName: string, validationError: string | any): void => {
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: validationError,
        }))
    }, [])
    const resetForm = (): void => {
        setIsEditing(false)
        resetRecordFormData(setFormData)
        resetRecordFormData(setFormErrors)
        setSelectedPdf(null)
        setSelectedCSV(null)
    }
    const handleChange = useCallback((fieldName: string, value: string) => {
        isError.isError = false
        setFormData(prevData => ({
            ...prevData,
            [fieldName]: value,
        }))
        setIsFormModified(true)
    }, [])
    const handleDateChange = useCallback((value: string) => {
        try {
            setDatePickerError({ isError: false, errorMessage: "" })
            const date = new Date(value)
            if (isNaN(date.getDate())) {
                setDatePickerError({ isError: true, errorMessage: "Invalid Date!" })
            } else {
                const formatedDate = convertDateToDDMMMYYYY(date)
                setBatchDate(formatedDate)
                setIsFormModified(true)
            }
        } catch (e) {
            console.log(e)
            setDatePickerError({
                isError: true,
                errorMessage: e instanceof Error ? e.message : "Invalid Date!",
            })
        }
    }, [])
    const onCloseProcess = (): void => {
        resetForm()
        setIsError({ isError: false, errorMessage: "" })
        onClose(false)
    }
    const onClearClick = (inputId: string) => {
        setFormData(prevData => ({
            ...prevData,
            [inputId]: "",
        }))
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [inputId]: "",
        }))
    }
    const handlePdfClick = () => {
        handleUploadFileClick(".pdf", setSelectedPdf, setFormData, null, "uploadPdfName")
    }
    const handleCSVClick = () => {
        handleUploadFileClick(".csv", setSelectedCSV, setFormData, null, "uploadCSVName")
    }
    const clearPdf = () => {
        console.log("unselect:", selectedPdf?.name)
        setFormData(prevData => ({
            ...prevData,
            uploadImageName: "",
        }))
        setSelectedPdf(null)
    }
    const clearCSV = () => {
        console.log("unselect:", selectedCSV?.name)
        setFormData(prevData => ({
            ...prevData,
            upload3DFileName: "",
        }))
        setSelectedCSV(null)
    }
    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault()
        try {
            setLoading(true)

            // Check if the form has been modified
            if (isEditing && !isFormModified && !selectedCSV && !selectedPdf) {
                setIsError({
                    isError: true,
                    errorMessage: "No changes made. Please update the form before submitting.",
                })
                setLoading(false)
                return
            }

            // Validate the form
            const errors: Record<string, string> = validateForm(formData)
            const hasErrors = Object.values(errors).some(error => error !== "")

            if (hasErrors || datePickerError.isError) {
                setIsError({
                    isError: true,
                    errorMessage: "There are validation errors, update the form texts",
                })
                setFormErrors(errors)
                setLoading(false) // Reset loading state
                return
            }

            const { manufacturer, purchaseOrder, description, partNumber } = formData
            const sensorBatchDto: CreateSensorBatch = {
                batchDate: batchDate,
                manufacturer: manufacturer,
                purchaseOrder: purchaseOrder,
                partNumber: partNumber,
                description: description,
                sensorBatchCsv: selectedCSV || new File([], "empty_file"),
                sensorBatchPdf: selectedPdf || new File([], "empty_file"),
                isVirtualSensorBatch: formData?.isVirtualSensorBatch === "true",
            }
            const requestResult: StoreResults = await (isEditing
                ? sensorBatchStore.editEntity({
                      ...sensorBatchDto,
                      id: sensorBatch?.id,
                  } as EditSensorBatch)
                : sensorBatchStore.createEntity(sensorBatchDto))

            if (!requestResult.passed) {
                setIsError({ isError: true, errorMessage: requestResult.message })
                setLoading(false)
                return
            }

            //Handle success
            console.log("Form submitted successfully!")
            resetForm() // Reset form data and errors
            const routeParam: NavigationModalOptions = {
                type: isEditing ? "edited" : "created",
            }
            onClose(true, requestResult.entityId, routeParam)
        } catch (error) {
            setIsError({
                isError: true,
                errorMessage: "Form submission error, please read logs",
            })
            console.error("Form submission error:", error)
        } finally {
            setLoading(false)
        }
    }

    const labels = {
        batchDate: formatMessage("sensorBatches.batchDate"),
        purchaseOrder: formatMessage("sensorBatches.purchaseOrder"),
        manufacturer: formatMessage("sensorBatches.manufacturer"),
        partNumber: formatMessage("sensorBatches.partNumber"),
        description: formatMessage("sensorBatches.description"),
        uploadPdfName: formatMessage("sensorBatches.uploadPdfName"),
        uploadCSVName: formatMessage("sensorBatches.uploadCSVName"),
    }
    const inputIdent: SensorBatchFormField[] = [
        {
            id: "manufacturer",
            label: labels.manufacturer,
            placeholder: `Type ${labels.manufacturer} here...`,
            type: "string",
            validate: validateText,
            isRequired: true,
        },
        {
            id: "purchaseOrder",
            label: labels.purchaseOrder,
            placeholder: `Type ${labels.purchaseOrder} here...`,
            type: "string",
            validate: validateText,
            isRequired: true,
        },
    ]
    const inputTechDetails: SensorBatchFormField[] = [
        {
            id: "partNumber",
            label: labels.partNumber,
            placeholder: `Type ${labels.partNumber} here...`,
            type: "string",
            validate: validateText,
            isRequired: true,
        },
        {
            id: "description",
            label: labels.description,
            placeholder: `Type ${labels.description} here...`,
            type: "string",
            validate: undefined,
            isRequired: false,
        },
        {
            id: "uploadPdfName",
            label: labels.uploadPdfName,
            placeholder: `Select from files...`,
            type: "string",
            onFileUpload: handlePdfClick,
            unSelectFile: clearPdf,
            isRequired: false,
        },
        {
            id: "uploadCSVName",
            label: labels.uploadCSVName,
            placeholder: `Select from files...`,
            type: "string",
            onFileUpload: handleCSVClick,
            unSelectFile: clearCSV,
            isRequired: false,
        },
    ]

    const renderInputs = (inputs: SensorBatchFormField[]): React.ReactNode => {
        return (
            <>
                {inputs.map(input => {
                    const { id, label, validate, type, placeholder, isRequired, pattern, step } = input
                    const clearIcon = formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={() => onClearClick(id)} />

                    const inputProps: InputProps = {
                        id,
                        label,
                        placeholder,
                        validate,
                        onValidation: (validationError: string | undefined) => handleValidation(id, validationError),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.value),
                        type,
                        error: formErrors[id],
                        heightClass: "small",
                        spanning: true,
                        required: isRequired,
                        uiRequired: isRequired,
                        icon: clearIcon,
                        value: formData[id],
                        pattern: pattern,
                        step: step,
                    }

                    return (
                        <span style={{ width: "15em" }} key={id}>
                            <Input {...inputProps} />
                        </span>
                    )
                })}
            </>
        )
    }

    const renderInputIdent = (): React.ReactNode => {
        return (
            <GridRow style={{ gap: "2em" }}>
                <span style={{ width: "15em" }}>
                    <DateTimeInput
                        label={labels.batchDate}
                        onChange={(value: string | any) => handleDateChange(value.toString())}
                        value={batchDate}
                        format='DD-MMM-YYYY'
                        timeFormat={false}
                        error={datePickerError.errorMessage}
                    />
                </span>
                {renderInputs(inputIdent)}
            </GridRow>
        )
    }

    const renderCheckbox = (): React.ReactNode => {
        const isVirtual = formData?.isVirtualSensorBatch === "true"
        return (
            <>
                <div className={styles.virtualSensorContainer}>
                    <span className={styles.virtualSensorLabel}>{formatMessage("sensorBatches.virtualSensor")}</span>
                    <div className={styles.virtualCheckboxContainer}>
                        <Checkbox
                            name='isVirtualSensorBatch'
                            checked={isVirtual}
                            required={false}
                            onChange={(event, data) => handleChange("isVirtualSensorBatch", (data?.checked === true).toString())}
                        />
                    </div>
                </div>
            </>
        )
    }

    const renderInputTechDetails = (): React.ReactNode => {
        return (
            <>
                <GridRow style={{ gap: "2em" }}>
                    {renderInputs(inputTechDetails.slice(0, 2))}
                    {inputTechDetails.slice(2, 4).map((input: SensorBatchFormField, index) => {
                        const { id, label, type, placeholder, onFileUpload, unSelectFile } = input
                        const icons = (
                            <>
                                {formData[id] && (
                                    <Icon className='icon-cross-circle-fill-16' onClick={unSelectFile} style={{ marginRight: "2px" }} />
                                )}
                                <Icon className='icon-upload-16' onClick={onFileUpload} />
                            </>
                        )
                        const inputProps: InputProps = {
                            id,
                            label,
                            placeholder,
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.value),
                            type,
                            error: formErrors[id],
                            heightClass: "small",
                            spanning: true,
                            onClick: onFileUpload,
                            value: formData[id],
                            icon: icons,
                            readOnly: true,
                        }
                        return (
                            <span style={{ width: "15em" }}>
                                <Input {...inputProps} />
                            </span>
                        )
                    })}
                    {renderCheckbox()}
                </GridRow>
            </>
        )
    }

    const rightPageButtons: ReactNode = (
        <>
            <Button disabled={isLoading} icon='icon-cross-circle-16' labelPosition='right' onClick={onCloseProcess} secondary>
                {formatMessage("sensorBatchModal.close")}
            </Button>
            <Button disabled={isLoading} loading={isLoading} icon='icon-save-16' labelPosition='right' onClick={handleSubmit} primary>
                {formatMessage("sensorBatchModal.save")}
            </Button>
        </>
    )
    const itemViewListContainerStyle = {
        marginTop: "var(--size-l)",
        padding: "0 var(--size-l)",
    }
    const itemViewSeparatorStyle = {
        height: "1px",
        borderBottom: "1px solid #B3B3B3",
        margin: "var(--size-s) 0 ",
    }

    return (
        <Dialog dimmer open={isOpen} size={"fullscreen"}>
            {isError.isError && (
                <DialogDescription>
                    <p style={{ color: "red" }}>{isError.errorMessage}</p>
                </DialogDescription>
            )}
            <PageTitle title={modalTitle} extraItems={rightPageButtons} />

            <div style={itemViewListContainerStyle}>
                <h4>
                    <FormattedMessage id='sensorBatchModal.sensorBatchIden' />
                </h4>
                <Grid columns={3} style={{ margin: "1em 0" }}>
                    {renderInputIdent()}
                </Grid>
                <div style={itemViewSeparatorStyle} />
                <h4>
                    <FormattedMessage id='sensorBatchModal.techDetails' />
                </h4>
                <Grid columns={3} style={{ margin: "1em 0" }}>
                    {renderInputTechDetails()}
                </Grid>
            </div>
            <div style={{ width: "100%", textAlign: "center", marginTop: "3em" }}>
                <Icon className='icon-info-o-24' /> Before you continue, you have to fill in all the required fields.
            </div>
        </Dialog>
    )
}

export default SensorBatchModal
